/**
 * LiveStatusTable - Live garment status tracking table
 *
 * Real-time tracking of test garments through the production workflow.
 * Polls the testing.getTestGarmentStatus endpoint every 5 seconds.
 *
 * Features for warehouse UX:
 * - h-14 row heights for glove-friendly touch targets
 * - Sticky table headers for scrolling
 * - Skeleton loading pattern
 * - Empty state with helpful message
 * - Status badges with color coding
 * - 5s polling when expanded/visible
 *
 * Columns:
 * - Garment ID (short)
 * - Order #
 * - SKU
 * - Status
 * - Bin
 * - Station
 * - Last Scan
 *
 * Phase 17: Testing Infrastructure
 */
import { skipToken } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileQuestion, RefreshCw, AlertCircle } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { formatOrderNumber } from '@threadheads/shared';

/** Status badge color mapping */
function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'PENDING':
    case 'LABEL_PRINTED':
      return 'outline';
    case 'PICKED':
    case 'IN_PRETREAT':
      return 'secondary';
    case 'CAUGHT':
    case 'IN_BIN':
      return 'default';
    case 'READY_TO_PACK':
    case 'PACKED':
      return 'default';
    case 'FAILED':
    case 'DISCARDED':
      return 'destructive';
    // SUPERSEDED: re-batch-replaced sticker — neutral/archived, NOT a failure, so
    // explicitly 'outline' (never destructive red).
    case 'SUPERSEDED':
      return 'outline';
    default:
      return 'outline';
  }
}

/** Format last scan time as relative */
function formatLastScan(timestamp: string | null): string {
  if (!timestamp) return '-';
  try {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  } catch {
    return '-';
  }
}

/** Shorten garment ID for display */
function shortenId(id: string): string {
  if (id.length <= 8) return id;
  return `${id.slice(0, 4)}...${id.slice(-4)}`;
}

export function LiveStatusTable() {
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id;

  // Query test garments with 5s polling
  const { data, isLoading, isError, error } = trpc.testing.getTestGarmentStatus.useQuery(
    locationId
      ? { locationId, limit: 50 }
      : skipToken,
    {
      refetchInterval: 5_000, // 5s polling for live updates
      refetchIntervalInBackground: false, // Pause when tab hidden
      staleTime: 2_000, // Consider fresh for 2s
    }
  );

  // Loading state with skeleton
  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Loading test garments...
        </div>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-[100px]">ID</TableHead>
                <TableHead>Order #</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Bin</TableHead>
                <TableHead>Station</TableHead>
                <TableHead className="text-right">Last Scan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3].map((i) => (
                <TableRow key={i} className="h-14">
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <AlertCircle className="h-12 w-12 text-destructive/50 mb-3" />
          <p className="text-sm text-destructive">
            Failed to load test garments
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {error?.message ?? 'Unknown error'}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <FileQuestion className="h-12 w-12 text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">
            No test garments found at this location.
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Use Seed Controls to create a test batch.
          </p>
        </div>
      </div>
    );
  }

  // Data table
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className="h-3 w-3" />
          Polling every 5s
        </div>
        <div className="text-xs text-muted-foreground">
          {data.length} garment{data.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="border rounded-lg overflow-auto max-h-[25vh]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-[100px]">ID</TableHead>
              <TableHead>Order #</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Bin</TableHead>
              <TableHead>Station</TableHead>
              <TableHead className="text-right">Last Scan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((garment) => (
              <TableRow
                key={garment.id}
                className={cn(
                  'h-14',
                  garment.isRedo && 'bg-yellow-50/50 dark:bg-yellow-950/20'
                )}
              >
                <TableCell className="font-mono text-xs">
                  {shortenId(garment.id)}
                  {garment.isRedo && (
                    <Badge variant="outline" className="ml-1 text-[10px] px-1">
                      REDO
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {formatOrderNumber(garment.orderName)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {garment.sku}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusVariant(garment.status)}>
                    {garment.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {garment.bin ?? '-'}
                </TableCell>
                <TableCell className="text-sm">
                  {garment.station}
                </TableCell>
                <TableCell className="text-right text-xs text-muted-foreground">
                  {formatLastScan(garment.lastScan)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
